import React, { useState, useRef, useEffect } from "react";

import GoogleMap from "./GoogleMap";
import "./map.scss";
import CustomOverlay from "../../../SearchListings/components/CustomOverlay";
import PropertyCard from "./PropertyCard";
import { formatSimplePrice } from "../../../SearchListings/components/helpers";
import { format } from "date-fns";
import ResultsCount from "../ResultsCount";

const Map = ({
  searchConfig,
  locationQuery,
  setLocationQuery,
  properties,
  radius,
  onSelect,
  selected,
  onClickProperty,
  addProspectAsContact,
  callProspect,
  setPropertyForDetails,
  areaGeometry,
  resultsCount,
  limit,
}) => {
  const container = useRef(null);
  const [heightOffset, setHeightOffset] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const onDrawButtonClick = () => {
    setIsDrawing(!isDrawing);
    setLocationQuery(null);
  };

  const onPolygonDrawn = (polygon) => {
    setIsDrawing(false);
    setLocationQuery(polygon);
  };

  const onClickMarker = (index) => {
    if (index === selectedProperty) {
      deselectProperty();
      return;
    }

    setSelectedProperty(index);
    onClickProperty(properties[index].id);
  };

  const deselectProperty = () => {
    setSelectedProperty(null);
    onClickProperty(null);
  };

  useEffect(() => {
    const offset = container.current.getBoundingClientRect().top || 0;
    // 8 being the bottom padding
    setHeightOffset(offset + 8);
  }, []);

  useEffect(() => {
    deselectProperty();
  }, [locationQuery]);

  return (
    <div ref={container} className="tw-relative">
      <GoogleMap
        className="tw-w-full tw-rounded-[8px] opportunities-map"
        style={{ height: `calc(100vh - ${heightOffset}px)` }}
        isDrawing={isDrawing}
        locationQuery={locationQuery}
        radius={radius}
        setLocationQuery={onPolygonDrawn}
        onClick={deselectProperty}
        areaGeometry={areaGeometry}
      >
        {properties?.length > 0 &&
          properties.map((property, i) => (
            <CustomOverlay
              key={property.id}
              position={{ lat: property.lat, lng: property.lng }}
              pixelOffset={{ x: -32, y: -34 }}
              zIndex={selectedProperty === i ? 10 : "initial"}
            >
              <PropertyMarker
                value={
                  searchConfig.typeName === "Preforeclosure"
                    ? format(new Date(property.statusDate), "L/d")
                    : formatSimplePrice(property.listPrice) || "N/A"
                }
                price={property.listPrice}
                date={property.statusDate}
                onClick={() => onClickMarker(i)}
                isSelected={selectedProperty === i}
              />
            </CustomOverlay>
          ))}
        {properties?.length > 0 && selectedProperty !== null && properties[selectedProperty] && (
          <CustomOverlay
            key={properties[selectedProperty].id}
            position={{ lat: properties[selectedProperty].lat, lng: properties[selectedProperty].lng }}
            pixelOffset={{ x: -150, y: -324 }}
            allowReposition={true}
            elementSize={{ width: 279, height: 284 }}
          >
            <PropertyCard
              config={searchConfig}
              property={properties[selectedProperty]}
              isSelected={selected.includes(properties[selectedProperty].id)}
              select={() => onSelect(properties[selectedProperty])}
              addProspectAsContact={addProspectAsContact}
              callProspect={callProspect}
              setPropertyForDetails={setPropertyForDetails}
            />
          </CustomOverlay>
        )}
      </GoogleMap>
      <div className="tw-absolute tw-top-24px tw-left-12px">
        <button
          className="tw-relative tw-font-bold tw-border-solid tw-border-gray-30 tw-bg-white tw-rounded-full tw-text-center tw-uppercase tw-border-2px tw-px-18px tw-py-6px tw-text-14d hover:tw-bg-gray-10"
          onClick={onDrawButtonClick}
        >
          {isDrawing ? "Clear" : "Draw Area"}
        </button>
      </div>
      {resultsCount > 0 && (
        <div className="tw-absolute tw-bottom-12px tw-left-1/2 tw--translate-x-1/2 tw-bg-white tw-rounded-[8px] tw-border tw-border-solid tw-p-12px tw-border-gray-10 tw-shadow-[0_2px_8px_0_rgba(213,213,213,0.5)]">
          <ResultsCount resultCount={resultsCount} limit={limit} className="tw-text-gray-75" />
        </div>
      )}
    </div>
  );
};

export default Map;

const PropertyMarker = ({ value, onClick, isSelected }) => {
  const clicks = (e) => {
    e.preventDefault();

    onClick();
  };

  const selectedStyling = isSelected
    ? "tw-border-theme-primary tw-text-theme-primary before:tw-border-t-theme-primary"
    : "tw-border-gray-30 tw-text-gray-75 before:tw-border-t-gray-30";

  const stateStyles = `
    before:tw-content-[''] before:tw-block before:tw-absolute before:tw-h-0 before:tw-w-0 before:tw-border-solid before:tw-border-transparent
    after:tw-content-[''] after:tw-block after:tw-absolute after:tw-h-0 after:tw-w-0 after:tw-border-solid after:tw-border-transparent
    before:tw-top-full before:tw-border-[7px]
    after:tw-top-[95%] after:tw-border-[7px] after:tw-border-t-white
    hover:tw-border-theme-primary hover:tw-text-theme-primary hover:tw-bg-theme-primary-disabled
    hover:before:tw-border-t-theme-primary hover:after:tw-border-t-theme-primary-disabled
    active:tw-border-theme-primary active:tw-text-white active:tw-bg-theme-primary
    active:before:tw-border-t-theme-primary active:after:tw-border-t-theme-primary
  `;

  return (
    <div
      onClick={clicks}
      className={`tw-relative tw-flex tw-cursor-pointer tw-justify-center tw-bg-white tw-px-8px tw-py-3px tw-items-center tw-rounded-666px tw-border-1px tw-border-solid ${stateStyles} ${selectedStyling}`}
    >
      <span className="tw-text-14px tw-font-sans tw-font-semibold">{value}</span>
    </div>
  );
};
